import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faPlus, 
  faEdit, 
  faTrash, 
  faEye,
  faUserShield,
  faUser,
  faFilter,
  faSortUp,
  faSortDown
} from '@fortawesome/free-solid-svg-icons';
import '../styles/AdminMembers.css';

const AdminMembers = () => {
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);

  // Mock data - In real app, this would come from your backend
  const mockMembers = [
    {
      id: 1,
      name: 'Nguyễn Văn A',
      email: 'nguyenvana@email.com',
      role: 'admin',
      status: 'active',
      joinDate: '2024-01-15',
      lastLogin: '2024-01-20',
      avatar: null
    },
    {
      id: 2,
      name: 'Trần Thị B',
      email: 'tranthib@email.com',
      role: 'member',
      status: 'active',
      joinDate: '2024-01-18',
      lastLogin: '2024-01-19',
      avatar: null
    },
    {
      id: 3,
      name: 'Lê Văn C',
      email: 'levanc@email.com',
      role: 'member',
      status: 'inactive',
      joinDate: '2024-01-10',
      lastLogin: '2024-01-15',
      avatar: null
    },
    {
      id: 4,
      name: 'Phạm Thị D',
      email: 'phamthid@email.com',
      role: 'moderator',
      status: 'active',
      joinDate: '2024-01-12',
      lastLogin: '2024-01-20',
      avatar: null
    },
    {
      id: 5,
      name: 'Hoàng Văn E',
      email: 'hoangvane@email.com',
      role: 'member',
      status: 'active',
      joinDate: '2024-01-20',
      lastLogin: '2024-01-20',
      avatar: null
    }
  ];

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setMembers(mockMembers);
      setFilteredMembers(mockMembers);
      setLoading(false);
    }, 1000);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    filterAndSortMembers();
  }, [searchTerm, filterRole, sortField, sortDirection, members]); // eslint-disable-line react-hooks/exhaustive-deps

  const filterAndSortMembers = () => {
    let filtered = members.filter(member => {
      const matchesSearch = member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          member.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = filterRole === 'all' || member.role === filterRole;
      return matchesSearch && matchesRole;
    });

    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      if (sortField === 'joinDate' || sortField === 'lastLogin') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredMembers(filtered);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleAddMember = () => {
    setEditingMember(null);
    setShowEditModal(true);
  };

  const handleEditMember = (member) => {
    setEditingMember({...member});
    setShowEditModal(true);
  };

  const handleSaveMember = () => {
    if (editingMember) {
      const updatedMembers = members.map(member => 
        member.id === editingMember.id ? editingMember : member
      );
      setMembers(updatedMembers);
      setShowEditModal(false);
      setEditingMember(null);
    }
  };

  const handleDeleteMember = (memberId) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa thành viên này?')) {
      const updatedMembers = members.filter(member => member.id !== memberId);
      setMembers(updatedMembers);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return faUserShield;
      case 'moderator':
        return faUserShield;
      default:
        return faUser;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'red';
      case 'moderator':
        return 'orange';
      default:
        return 'blue';
    }
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'green' : 'gray';
  };

  if (loading) {
    return (
      <div className="members-loading">
        <div className="loading-spinner"></div>
        <p>Đang tải danh sách thành viên...</p>
      </div>
    );
  }

  return (
    <div className="admin-members">
      <div className="members-header">
        <div>
          <h2>Quản lý thành viên</h2>
          <p>Quản lý tài khoản và quyền hạn của thành viên</p>
        </div>
        <button className="add-member-btn" onClick={handleAddMember}>
          <FontAwesomeIcon icon={faPlus} />
          Thêm thành viên
        </button>
      </div>

      {/* Filters and Search */}
      <div className="members-controls">
        <div className="search-box">
          <FontAwesomeIcon icon={faSearch} />
          <input
            type="text"
            placeholder="Tìm kiếm theo tên hoặc email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filter-controls">
          <div className="filter-group">
            <FontAwesomeIcon icon={faFilter} />
            <select 
              value={filterRole} 
              onChange={(e) => setFilterRole(e.target.value)}
            >
              <option value="all">Tất cả vai trò</option>
              <option value="admin">Admin</option>
              <option value="moderator">Moderator</option>
              <option value="member">Thành viên</option>
            </select>
          </div>
        </div>
      </div>

      {/* Members Table */}
      <div className="members-table-container">
        <table className="members-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('name')} className="sortable">
                Thành viên
                {sortField === 'name' && (
                  <FontAwesomeIcon icon={sortDirection === 'asc' ? faSortUp : faSortDown} />
                )}
              </th>
              <th onClick={() => handleSort('role')} className="sortable">
                Vai trò
                {sortField === 'role' && (
                  <FontAwesomeIcon icon={sortDirection === 'asc' ? faSortUp : faSortDown} />
                )}
              </th>
              <th onClick={() => handleSort('status')} className="sortable">
                Trạng thái
                {sortField === 'status' && (
                  <FontAwesomeIcon icon={sortDirection === 'asc' ? faSortUp : faSortDown} />
                )}
              </th>
              <th onClick={() => handleSort('joinDate')} className="sortable">
                Ngày tham gia
                {sortField === 'joinDate' && (
                  <FontAwesomeIcon icon={sortDirection === 'asc' ? faSortUp : faSortDown} />
                )}
              </th>
              <th onClick={() => handleSort('lastLogin')} className="sortable">
                Đăng nhập cuối
                {sortField === 'lastLogin' && (
                  <FontAwesomeIcon icon={sortDirection === 'asc' ? faSortUp : faSortDown} />
                )}
              </th>
              <th>Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {filteredMembers.map(member => (
              <tr key={member.id}>
                <td>
                  <div className="member-info">
                    <div className="member-avatar">
                      {member.avatar ? (
                        <img src={member.avatar} alt={member.name} />
                      ) : (
                        <div className="avatar-placeholder">
                          {member.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="member-details">
                      <div className="member-name">{member.name}</div>
                      <div className="member-email">{member.email}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <div className={`role-badge ${getRoleColor(member.role)}`}>
                    <FontAwesomeIcon icon={getRoleIcon(member.role)} />
                    <span>
                      {member.role === 'admin' ? 'Admin' :
                       member.role === 'moderator' ? 'Moderator' : 'Thành viên'}
                    </span>
                  </div>
                </td>
                <td>
                  <div className={`status-badge ${getStatusColor(member.status)}`}>
                    {member.status === 'active' ? 'Hoạt động' : 'Không hoạt động'}
                  </div>
                </td>
                <td>{new Date(member.joinDate).toLocaleDateString('vi-VN')}</td>
                <td>{new Date(member.lastLogin).toLocaleDateString('vi-VN')}</td>
                <td>
                  <div className="action-buttons">
                    <button 
                      className="action-btn view"
                      title="Xem chi tiết"
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </button>
                    <button 
                      className="action-btn edit"
                      title="Chỉnh sửa"
                      onClick={() => handleEditMember(member)}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button 
                      className="action-btn delete"
                      title="Xóa"
                      onClick={() => handleDeleteMember(member.id)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Member Modal */}
      {showEditModal && editingMember && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Chỉnh sửa thành viên</h3>
              <button 
                className="modal-close"
                onClick={() => setShowEditModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Tên thành viên</label>
                <input
                  type="text"
                  value={editingMember.name}
                  onChange={(e) => setEditingMember({
                    ...editingMember,
                    name: e.target.value
                  })}
                />
              </div>
              
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={editingMember.email}
                  onChange={(e) => setEditingMember({
                    ...editingMember,
                    email: e.target.value
                  })}
                />
              </div>
              
              <div className="form-group">
                <label>Vai trò</label>
                <select
                  value={editingMember.role}
                  onChange={(e) => setEditingMember({
                    ...editingMember,
                    role: e.target.value
                  })}
                >
                  <option value="member">Thành viên</option>
                  <option value="moderator">Moderator</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Trạng thái</label>
                <select
                  value={editingMember.status}
                  onChange={(e) => setEditingMember({
                    ...editingMember,
                    status: e.target.value
                  })}
                >
                  <option value="active">Hoạt động</option>
                  <option value="inactive">Không hoạt động</option>
                </select>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-secondary"
                onClick={() => setShowEditModal(false)}
              >
                Hủy
              </button>
              <button 
                className="btn-primary"
                onClick={handleSaveMember}
              >
                Lưu thay đổi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMembers;